import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Beaker, Award, Package } from "lucide-react";
import SEO from "../../components/SEO";
import { MEDIA, REGISTRATION_LINKS } from "../../lib/constants";

export default function AltSolCannabisDC() {
  return (
    <>
      <SEO
        title="Alt Sol Cannabis in DC: Duct Tape, Blue Sharpies, Hashburger & More"
        description="Alt Sol Cannabis is one of DC's most trusted small-batch flower brands. Duct Tape, Blue Sharpies, Hashburger — hand-finished jars in stock at District Wellness Center on Georgia Ave NW."
        path="/blog/alt-sol-cannabis-dc"
        ogType="article"
        ogImage={MEDIA.altSolJars}
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "Alt Sol Cannabis in DC", path: "/blog/alt-sol-cannabis-dc" },
        ]}
        article={{
          publishedTime: "2026-09-15",
          modifiedTime: "2026-09-15",
          section: "Brand Spotlight",
          image: MEDIA.altSolJars,
        }}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
              Brand Spotlight · DC Cultivator
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              <em className="text-brand">Alt Sol</em> — hand-finished flower for DC.
            </h1>
            <p className="mt-6 text-ink-muted text-base sm:text-lg max-w-xl leading-relaxed">
              Alt Sol has quietly built one of the strongest flower reputations
              in the District. Small batches, careful hangs, terpene-forward
              phenos, and jars that actually look like the flower inside is
              worth the price. Here&rsquo;s why the brand keeps landing on the
              DWC menu — and which drops are worth watching.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="alt-cta-menu"
              >
                Shop Alt Sol <ArrowUpRight size={14} />
              </Link>
              <a
                href={REGISTRATION_LINKS.medical}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-ink/15 text-ink hover:border-brand hover:text-brand px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="alt-cta-register"
              >
                Register for Medical Cannabis <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <img
              src={MEDIA.altSolJars}
              alt="Alt Sol Cannabis flower jars — Duct Tape, Blue Sharpies, Hashburger — at District Wellness Center, Washington DC"
              className="w-full h-[520px] object-cover border border-black/10"
              loading="lazy"
              data-testid="alt-hero-image"
            />
          </div>
        </div>
      </section>

      {/* ARTICLE */}
      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 font-sans text-ink-muted">
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-0 mb-5">
            Who is Alt Sol Cannabis?
          </h2>
          <p className="text-lg leading-relaxed">
            Alt Sol is a DC-licensed cultivator focused on <strong>small-batch,
            hand-finished flower</strong>. Instead of chasing volume, the team
            leans into slow cure times, careful trim, and phenotype selection.
            The result is flower that hits the jar looking like flower —
            frosty, aromatic, and structured — not the dry, over-handled
            product that&rsquo;s become common in a lot of markets.
          </p>
          <p className="text-lg leading-relaxed mt-4">
            You&rsquo;ll spot Alt Sol on the shelf immediately: matte black
            jars with the signature gold script and a color-coded holographic
            sticker for each strain. It&rsquo;s clean, quiet packaging that
            lets the flower do the talking.
          </p>

          {/* Feature grid */}
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <div className="p-6 bg-white border border-black/10">
              <Beaker size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Testing</div>
              <div className="font-serif text-xl text-ink">Lab-tested each batch</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Award size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Craft</div>
              <div className="font-serif text-xl text-ink">Hand-finished, slow cure</div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Package size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Format</div>
              <div className="font-serif text-xl text-ink">3.5g flower jars</div>
            </div>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            The strains to know
          </h2>

          <h3 className="font-serif text-2xl text-ink mt-8 mb-3">
            Duct Tape
          </h3>
          <p className="text-base leading-relaxed">
            A gassy, heavy hybrid — think Original Glue crossed with Do-Si-Dos
            energy. Sticky, resinous, and long-lasting. Duct Tape is the drop
            regulars text us about on Fridays.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-8 mb-3">
            Blue Sharpies
          </h3>
          <p className="text-base leading-relaxed">
            Sweet fruit up front, sharper gas on the exhale. Blue Sharpies is
            an easy hybrid that plays well with almost every occasion —
            afternoon walk, dinner with friends, late movie.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-8 mb-3">
            Hashburger
          </h3>
          <p className="text-base leading-relaxed">
            Deep, savory, hash-forward. Hashburger is the &ldquo;stop
            everything and taste this&rdquo; jar — dense buds, dark greens
            with amber pistils, and a body-first high that lands slow and
            stays a while.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Why Alt Sol keeps landing on the DWC menu
          </h2>
          <p className="text-lg leading-relaxed">
            The bar for landing on a DWC shelf is simple: <strong>would our
            budtenders buy this with their own money?</strong> Alt Sol clears
            that bar. Batches show up with intact trichomes, honest terpene
            noses, and lab work that matches the jar. Patients notice — and
            they come back for the next drop.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            How to grab an Alt Sol jar
          </h2>
          <ol className="space-y-3 text-lg list-decimal pl-6">
            <li>Check the live menu — Alt Sol drops move quickly.</li>
            <li>Reserve for pickup or add to a same-day delivery order.</li>
            <li>Bring a valid 21+ ID and your medical cannabis registration.</li>
          </ol>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="alt-menu-check"
            >
              See what&rsquo;s in stock <ArrowUpRight size={14} />
            </Link>
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="alt-register-cta"
            >
              Register for Medical Cannabis <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </article>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Small-batch DC flower. Real prices.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Come by 4915 Georgia Ave NW and ask our patient specialists which
            Alt Sol drops are on the shelf today. We&rsquo;ll pop the jar so
            you can smell it before you commit.
          </p>
          <div className="mt-10">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="alt-final-menu"
            >
              Shop the live menu <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
